// Privacy-first telemetry (spec §2): PAGE events only — never input buffers, diff
// content, values, filenames, or URLs. Beacons to a SAME-ORIGIN Pages Function
// (/api/e) which forwards to Workers Analytics Engine, so `connect-src 'self'`
// stays intact and no third party ever sees a request. Cookieless; honours DNT;
// no-ops in dev (logs instead). See docs/analytics.md for the schema + event catalogue.
const ENDPOINT = '/api/e';
export const SITE = 'cruxdiff'; // distinguishes this property inside the shared dataset
const PROD = !!(import.meta.env && import.meta.env.PROD);

// Event names are allow-listed on BOTH sides of the beacon. The collector is the
// final trust boundary; this client copy prevents typos or injected names from
// leaving the page at all.
const EVENTS = new Set([
  'page_view',
  'visit_recency',
  'share_open',
  'input_provided',
  'format_detected',
  'sample_try',
  'compare_click',
  'compare_result',
  'compare_error',
  'view_switch',
  'scope_switch',
  'wrap_toggle',
  'ignore_rule_applied',
  'share_created',
  'export',
  'pwa_install',
  'house_ad_view',
  'house_ad_click',
  'client_error',
  'format_unsupported',
  // --- scale, Parquet, and Change Analysis ---------------------------------
  'compare_size',
  'dataset_load',
  'viewer_open',
  'viewer_reject',
  'viewer_export',
  'analysis_shown',
  'analysis_expanded',
  'analysis_ignore',
  'analysis_undo',
  // --- /pro interest funnel -----------------------------------------------
  'pro_form_start', // first MEANINGFUL edit, not focus
  'pro_submit', // client validation passed and a request went out
  'pro_submit_ok', // the collector confirmed storage — never optimistic
  'pro_submit_fail', // bounded category only, never the server's words
]);

// These are intentionally small, closed vocabularies. Error messages, stacks,
// filenames and input content are never valid analytics dimensions (G33).
export const CLIENT_ERROR_CODES = Object.freeze([
  'engine_throw',
  'worker_timeout',
  'worker_spawn_fail',
  'render_throw',
]);
export const UNSUPPORTED_FORMATS = Object.freeze([
  'parquet',
  'xlsx',
  'xml',
  'pdf',
  'docx',
  'image',
  'binary',
  'json',
  'yaml',
  'csv',
  'tsv',
  'other',
]);
// Bucketed record scale. Raw counts are content-adjacent for a known dataset and
// are useless for trends anyway; five buckets answer "50 rows or 500,000?".
export const SIZE_BUCKETS = Object.freeze(['xs', 's', 'm', 'l', 'xl']);
export const DATASET_SHAPES = Object.freeze(['single', 'multi', 'folder']);
// Why a selection was refused. Reasons only — never the path, name or message.
// Only the reasons the loader can actually tell apart without reading an error
// MESSAGE — which is user-facing text and can quote a path. Splitting 'other'
// into corrupt/limit would need typed errors from the dataset reader.
export const REJECT_REASONS = Object.freeze(['format', 'oversize', 'other']);
export const EXPORT_SCOPES = Object.freeze(['all', 'filtered']);
// Which threshold arm opened the Change Analysis panel (spec §8.7: ≥150 records
// OR ≥50 differences) — the one number that says whether those figures are right.
export const ANALYSIS_TRIGGERS = Object.freeze(['records', 'differences']);
export const ANALYSIS_SECTIONS = Object.freeze(['fields', 'ignore', 'rules', 'schema']);
// "one" vs "all" is encoded in the enum rather than carried in `extra`, because a
// bounded event deliberately loses every other dimension (see sanitizeEvent).
export const IGNORE_SCOPES = Object.freeze(['one', 'all']);
// Why a Pro submission did not land. Categories, never a message: the server's
// words can quote the statement, and the statement quotes what was typed.
export const SUBMIT_FAILURES = Object.freeze(['network', 'ratelimit', 'server', 'invalid', 'unconfigured']);

// Events whose `kind` is a CLOSED vocabulary. Registering an event here is the
// strongest privacy guarantee available: the collector then stores its kind and
// nothing else, so no field name, path or count can ride along even by mistake.
// Universal vocabulary, documented beside the event since launch but never
// enforced — so a typo would have written an unqueryable bucket into the shared
// dataset. Enforced now on both sides; propagate to the suite template.
export const RECENCY_BUCKETS = Object.freeze(['new', 'd1', 'd2-7', 'd8+']);
const EVENT_KINDS = new Map([
  ['visit_recency', new Set(RECENCY_BUCKETS)],
  ['client_error', new Set(CLIENT_ERROR_CODES)],
  ['format_unsupported', new Set(UNSUPPORTED_FORMATS)],
  ['compare_size', new Set(SIZE_BUCKETS)],
  ['dataset_load', new Set(DATASET_SHAPES)],
  ['viewer_open', new Set(DATASET_SHAPES)],
  ['viewer_reject', new Set(REJECT_REASONS)],
  ['viewer_export', new Set(EXPORT_SCOPES)],
  ['analysis_shown', new Set(ANALYSIS_TRIGGERS)],
  ['analysis_expanded', new Set(ANALYSIS_SECTIONS)],
  ['analysis_ignore', new Set(IGNORE_SCOPES)],
  ['pro_submit_fail', new Set(SUBMIT_FAILURES)],
]);

// Page classes for page_view. The PATH (blob6) already distinguishes one landing
// page from another, so this stays a handful of classes rather than a slug list.
export const PAGE_IDS = Object.freeze(['home', 'viewer', 'privacy', 'about', '404', 'landing', 'pro']);

// The ONLY property keys that may leave the page — short strings / numbers,
// never content. Anything a caller passes outside this set is dropped.
const ALLOWED = ['format', 'kind', 'extra', 'v'];

/** Whitelist + clamp props so no content can ever be exfiltrated. */
export function sanitizeProps(props = {}) {
  const out = {};
  for (const k of ALLOWED) {
    if (props[k] == null) continue;
    out[k] = k === 'v' ? Number(props[k]) || 0 : String(props[k]).slice(0, 96);
  }
  return out;
}

/** Validate an event and apply its event-specific bounded vocabulary. */
export function sanitizeEvent(event, props = {}) {
  const name = String(event);
  if (!EVENTS.has(name)) return null;
  const clean = sanitizeProps(props);
  const kinds = EVENT_KINDS.get(name);
  if (!kinds) return { event: name, props: clean };
  if (!kinds.has(clean.kind)) return null;
  // Sensitive diagnostic events carry ONE optional dimension only: their closed
  // kind enum. Even otherwise-allowed generic props are dropped for these events.
  return { event: name, props: { kind: clean.kind } };
}

function referrerHost() {
  try {
    return typeof document !== 'undefined' && document.referrer ? new URL(document.referrer).host : '';
  } catch {
    return '';
  }
}

function dnt() {
  return typeof navigator !== 'undefined' && (navigator.doNotTrack === '1' || navigator.globalPrivacyControl === true);
}

/** Record a page/interaction event. Fire-and-forget; never throws. */
export function track(event, props = {}) {
  const clean = sanitizeEvent(event, props);
  if (!clean) return false;
  if (!PROD) {
    if (typeof console !== 'undefined') console.debug('[analytics]', clean.event, clean.props);
    return true;
  }
  try {
    if (dnt()) return false;
    const body = JSON.stringify({
      e: clean.event,
      site: SITE,
      path: location.pathname, // NEVER href/hash — the share fragment IS user data
      ref: referrerHost(),
      ...clean.props,
    });
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon) navigator.sendBeacon(ENDPOINT, blob);
    else fetch(ENDPOINT, { method: 'POST', body, headers: { 'content-type': 'application/json' }, keepalive: true }).catch(() => {});
    return true;
  } catch {
    /* telemetry must never break the app */
    return false;
  }
}

export const trackClientError = (code) => track('client_error', { kind: code });
export const trackUnsupportedFormat = (format) => track('format_unsupported', { kind: format });

/** page_view for one of the known page classes. Unknown ids are dropped. */
export const trackPageView = (id) => (PAGE_IDS.includes(id) ? track('page_view', { extra: id }) : false);

// --- retention, without an identifier ---------------------------------------
// The ONLY thing stored is a date string: no id, no counter, no visit history, so
// two people on one browser are indistinguishable and nothing links a return to a
// previous session's events. Gated on DNT/GPC BEFORE storage is touched — the
// privacy page promises the date is neither read nor sent, and track()'s own
// gate comes too late to keep the first half of that (G113). Wrapped because
// storage throws in private windows and with site data blocked (REL-056). Fires
// once per session on its first page.
const VISIT_KEY = 'cruxdiff:last-visit';
const SESSION_KEY = 'cruxdiff:recency-sent';

export function visitRecency(previous, today = new Date()) {
  if (!previous) return 'new';
  const days = Math.floor((Date.parse(`${today.toISOString().slice(0, 10)}T00:00:00Z`) - Date.parse(`${previous}T00:00:00Z`)) / 86400000);
  if (!Number.isFinite(days) || days < 0) return 'new';
  if (days <= 1) return 'd1'; // same-day return counts as d1; there is no d0
  if (days <= 7) return 'd2-7';
  return 'd8+';
}

export function trackVisitRecency() {
  if (dnt()) return false;
  let previous = null;
  let already = false;
  try {
    already = sessionStorage.getItem(SESSION_KEY) === '1';
    previous = localStorage.getItem(VISIT_KEY);
  } catch {
    return false; // storage unavailable — skip silently, never break the page
  }
  if (already) return false;
  const kind = visitRecency(previous);
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
    localStorage.setItem(VISIT_KEY, new Date().toISOString().slice(0, 10));
  } catch {
    /* recording the visit is best-effort; the beacon below still stands */
  }
  return track('visit_recency', { kind });
}
